import {isFilled} from '@yandex-delivery/utils'
import clsx from 'clsx'
import type {FC} from 'react'
import {useCallback, useMemo, useState} from 'react'
import type {DropResult} from 'react-beautiful-dnd'
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'
import {useFormContext} from 'react-hook-form'

import * as styles from '../../RouteInfo.classes'
import type {AddressInfoProps} from '../../RouteInfo.types'
import {useRemovePointHandlers} from '../../useRemovePointHandlers'
import {DraggableRouteItem} from '../DraggableRouteItem/DraggableRouteItem'

export type Props = Pick<AddressInfoProps, 'addresses' | 'setAddresses' | 'fullscreen'>

const reorder = (list: string[], startIndex: number, endIndex?: number) => {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)

  if (removed) {
    result.splice(endIndex ?? startIndex, 0, removed)
  }

  return result
}

export const DraggableAddresses: FC<Props> = props => {
  const {addresses, setAddresses, fullscreen} = props
  const [order, setOrder] = useState<string[]>(addresses.map(item => item.id))
  const removeHandlers = useRemovePointHandlers({
    addresses,
    setAddresses,
  })
  const {setValue, watch} = useFormContext()

  const sortedRouteByOrder = useMemo(
    () => order.map(id => addresses.find(address => address.id === id)).filter(isFilled),
    [order, addresses],
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (result.source.index === result.destination?.index) {
        return
      }

      const newSortOrder = reorder(order, result.source.index, result.destination?.index)
      setOrder(newSortOrder)
      const sortedAddressesByOrder = newSortOrder
        .map(id => addresses.find(address => address.id === id))
        .filter(isFilled)
      setAddresses(sortedAddressesByOrder)

      if (watch().hasOwnProperty('is_route_optimized')) {
        setValue('is_route_optimized', false)
      }
    },
    [order, setAddresses, watch, addresses, setValue],
  )

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={clsx(styles.addressesContainer, {[styles.fullscreenAddressesContainer]: fullscreen})}>
        <Droppable droppableId="drag-route">
          {droppableProvided => (
            <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
              {sortedRouteByOrder.map(({address, id}, index) => {
                const isSource = index === 0

                if (isSource) {
                  return (
                    <DraggableRouteItem
                      key={id}
                      address={address}
                      index={index}
                      isSource={true}
                      removable={false}
                      removeHandlers={removeHandlers}
                    />
                  )
                }

                return (
                  <Draggable key={id} draggableId={id} index={index}>
                    {draggableProvided => (
                      <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                        <DraggableRouteItem
                          key={id}
                          address={address}
                          draggableProvided={draggableProvided}
                          index={index}
                          isSource={isSource}
                          removable={!isSource && !fullscreen}
                          removeHandlers={removeHandlers}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  )
}
